// Panel oturumu: telefon + SMS kodu, üyelikler ve seçili firma (Control = servis firması, Business = kurum).
// Yetki (modül × eylem) ve özellik bayrakları bağlamdan okunur; menü, sekme ve ekranlar bunlara göre gizlenir.
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  can,
  CONTEXT_CHANGED_EVENT,
  type Action,
  type MeContext,
  type Module,
  type PermissionModule,
} from '@serota/domain';
import { api, ApiError, getCompanyId, onUnauthorized, setCompanyId, tokenStore } from './api';
import { queryClient } from './query';
import { realtime } from './realtime';

type Status = 'loading' | 'signedOut' | 'signedIn';

interface SessionApi {
  status: Status;
  context: MeContext | null;
  signIn: (phone: string, code: string) => Promise<MeContext>;
  signOut: () => Promise<void>;
  switchCompany: (companyId: string) => Promise<void>;
  refresh: () => Promise<void>;
  can: (module: PermissionModule, action?: Action) => boolean;
  flag: (module: Module) => boolean;
}

const Ctx = createContext<SessionApi | null>(null);

/**
 * Saklı firma seçimi artık üyelikte değilse (firma silindi, üyelik kaldırıldı, veritabanı yeniden kuruldu) API bağlamı
 * 403 ile reddeder; seçim bırakılıp bir kez daha istenir — yoksa kullanıcı giriş yapsa da panele hiç giremez.
 */
async function freshContext(): Promise<MeContext> {
  try {
    return await api.context();
  } catch (e) {
    if (e instanceof ApiError && e.status === 403 && getCompanyId()) {
      setCompanyId(null);
      return api.context();
    }
    throw e;
  }
}

/** Panel üyeliği olmayan kullanıcı (yolcu/şoför) panele giremez. */
export class NoMembershipError extends Error {
  constructor() {
    super('Bu numara bir servis firması veya kurum paneline bağlı değil. Yöneticinizden davet isteyin.');
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading');
  const [context, setContext] = useState<MeContext | null>(null);

  const load = useCallback(async () => {
    const c = await freshContext();
    // seçili firma artık üyelikte değilse ilk panel üyeliğine geç
    const panel = c.memberships.filter((m) => m.tenantType !== 'PLATFORM');
    if (!c.active || !panel.some((m) => m.companyId === c.active!.companyId)) {
      const first = panel[0];
      if (first && first.companyId !== getCompanyId()) {
        setCompanyId(first.companyId);
        const again = await api.context();
        setContext(again);
        return again;
      }
    }
    setContext(c);
    return c;
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      const t = await tokenStore.get();
      if (!t) return alive && setStatus('signedOut');
      try {
        const c = await load();
        if (!alive) return;
        setStatus(c.memberships.some((m) => m.tenantType !== 'PLATFORM') ? 'signedIn' : 'signedOut');
      } catch {
        if (alive) setStatus('signedOut');
      }
    })();
    const off = onUnauthorized(() => {
      setContext(null);
      setStatus('signedOut');
    });
    return () => {
      alive = false;
      off();
    };
  }, [load]);

  useEffect(() => {
    if (status === 'signedIn') realtime.start();
    else realtime.stop();
  }, [status]);

  // bağlam değişti (bayrak/plan): menü, sekme ve ekranlar aynı anda güncellenir
  useEffect(() => {
    if (status !== 'signedIn') return;
    realtime.subscribe('company');
    const off = realtime.on((e) => {
      if (e.type !== CONTEXT_CHANGED_EVENT) return;
      void load()
        .then(() => queryClient.invalidateQueries())
        .catch(() => undefined);
    });
    return () => {
      off();
      realtime.unsubscribe('company');
    };
  }, [status, load]);

  const signOut = useCallback(async () => {
    await api.logout();
    setCompanyId(null);
    queryClient.clear();
    realtime.stop();
    setContext(null);
    setStatus('signedOut');
  }, []);

  const value = useMemo<SessionApi>(
    () => ({
      status,
      context,
      async signIn(phone, code) {
        await api.verifyOtp(phone, code, 'console-web');
        const c = await load();
        if (!c.memberships.some((m) => m.tenantType !== 'PLATFORM')) {
          await api.logout();
          setContext(null);
          throw new NoMembershipError();
        }
        setStatus('signedIn');
        return c;
      },
      signOut,
      async switchCompany(id) {
        setCompanyId(id);
        queryClient.clear();
        // yeniden bağlan: mantıksal kanallar (ops, company) yeni firma bağlamıyla yeniden yetkilendirilir
        realtime.stop();
        await load();
        realtime.start();
      },
      async refresh() {
        await load().catch((e) => {
          if (!(e instanceof ApiError)) throw e;
        });
      },
      can: (module, action = 'view') => (context ? can(context.permissions, module, action) : false),
      flag: (module) => !!context?.flags[module],
    }),
    [status, context, load, signOut],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionApi {
  const v = useContext(Ctx);
  if (!v) throw new Error('SessionProvider eksik');
  return v;
}
